import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Clock, ArrowRight, Focus } from 'lucide-react';
import { formatUTCDate } from '../utils/dateUtils';
import { useDashboardData } from '../hooks/useDashboardData';
import { useDashboardFollowUps } from '../hooks/useDashboardFollowUps';
import { useUserSettings } from '../hooks/useUserSettings';
import { useAnonymization } from '../utils/anonymize';
import LoadingPage from '../components/ui/LoadingPage';
import { Skeleton } from '../components/ui/Skeleton';
import TimeRangeFilter from '../components/dashboard/TimeRangeFilter';

export default function Dashboard() {
  const [timeRange, setTimeRange] = useState(30);
  const { data, loading, error } = useDashboardData(timeRange);
  const { followUps, loading: followUpsLoading } = useDashboardFollowUps(timeRange);
  const { settings, loading: settingsLoading } = useUserSettings();
  const shouldAnonymize = settings?.anonymizing_mode || false;

  if (loading || followUpsLoading || settingsLoading) {
    return <LoadingPage message="Loading your dashboard..." />;
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Break the ice (Left) */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Clock className="w-6 h-6 text-primary-500 mr-2" />
              <h2 className="text-lg font-semibold">Break the ice</h2>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/focus/break-the-ice"
                className="text-sm text-gray-400 hover:text-primary-500 flex items-center gap-1"
              >
                <Focus className="w-4 h-4" />
                Focus
              </Link>
              <span className="text-sm text-gray-400">No contact in {timeRange} days</span>
            </div>
          </div>
          
          {data.needsFollowUp.length > 0 ? (
            <div className="space-y-4">
              {data.needsFollowUp.map(contact => (
                <Link
                  key={contact.id}
                  to={`/contacts/${contact.id}`}
                  className="flex items-center justify-between group"
                >
                  <div>
                    {settingsLoading ? (
                      <Skeleton className="h-6 w-32" />
                    ) : (
                      <p className="font-medium group-hover:text-primary-500">
                        {useAnonymization(contact.first_name, 'name', shouldAnonymize)}{' '}
                        {useAnonymization(contact.last_name, 'name', shouldAnonymize)}
                      </p>
                    )}
                    <p className="text-sm text-gray-400">
                      {settingsLoading ? (
                        <Skeleton className="h-4 w-48" />
                      ) : (
                        useAnonymization(
                          contact.work_email || contact.personal_email || 'No email',
                          'email',
                          shouldAnonymize
                        )
                      )}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No contacts need ice breaking</p>
          )}
        </div>

        {/* Recent Communications (Middle) */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Mail className="w-6 h-6 text-secondary-500 mr-2" />
              <h2 className="text-lg font-semibold">Ice Broken</h2>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/focus/ice-broken"
                className="text-sm text-gray-400 hover:text-primary-500 flex items-center gap-1"
              >
                <Focus className="w-4 h-4" />
                Focus
              </Link>
              <span className="text-sm text-gray-400">Last {timeRange} days</span>
            </div>
          </div>
          
          {data.recentCommunications.length > 0 ? (
            <div className="space-y-4">
              {data.recentCommunications.map(comm => (
                <Link
                  key={`${comm.id}-${comm.last_contact}`}
                  to={`/contacts/${comm.id}`}
                  className="flex items-center justify-between group"
                >
                  <div>
                    <p className="font-medium group-hover:text-primary-500">
                      {useAnonymization(comm.first_name, 'name', shouldAnonymize)}{' '}
                      {useAnonymization(comm.last_name, 'name', shouldAnonymize)}
                    </p>
                    <div className="flex items-center text-sm text-gray-400">
                      <span>{formatUTCDate(comm.last_contact)}</span>
                      <span className="mx-2">•</span>
                      <span>{comm.type === 'email' ? 'Email' : 'Manual Entry'}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No recent communications</p>
          )}
        </div>

        {/* Upcoming Follow-ups (Right) */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Clock className="w-6 h-6 text-primary-500 mr-2" />
              <h2 className="text-lg font-semibold">Follow Up</h2>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/focus/follow-ups"
                className="text-sm text-gray-400 hover:text-primary-500 flex items-center gap-1"
              >
                <Focus className="w-4 h-4" />
                Focus
              </Link>
              <span className="text-sm text-gray-400">Next {timeRange} days</span>
            </div>
          </div>
          
          {followUps.length > 0 ? (
            <div className="space-y-4">
              {followUps.map(followUp => (
                <Link
                  key={followUp.id}
                  to={`/contacts/${followUp.contact.id}`}
                  className="flex items-center justify-between group"
                >
                  <div>
                    <p className="font-medium group-hover:text-primary-500">
                      {useAnonymization(followUp.contact.first_name, 'name', shouldAnonymize)}{' '}
                      {useAnonymization(followUp.contact.last_name, 'name', shouldAnonymize)}
                    </p>
                    <p className="text-sm text-gray-400">
                      Due {formatUTCDate(followUp.due_date)}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No upcoming follow-ups</p>
          )}
        </div>
      </div>
    </div>
  );
}